import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, UserPlus, Mail, Phone, Edit, Trash2 } from 'lucide-react';

/* eslint-disable react/prop-types */
/**
 * Grid of active employee cards. Presentational — parent owns fetch & mutation callbacks.
 */
export function EmployeeList({ employees, permissions, loading, onEdit, onDelete, onInvite }) {
  if (loading) {
    return <p className="text-center text-muted-foreground">Loading team...</p>;
  }
  if (employees.length === 0) {
    return (
      <Card className="p-8 sm:p-12 text-center">
        <Shield className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg sm:text-xl font-semibold mb-2">No employees yet</h3>
        <p className="text-muted-foreground mb-6 text-sm sm:text-base">
          Invite a property manager or assistant to help you manage listings, respond to tenants, and more.
        </p>
        <Button data-testid="empty-invite-btn" onClick={onInvite} className="rounded-full bg-primary hover:bg-primary/90">
          <UserPlus className="w-4 h-4 mr-2" /> Send First Invite
        </Button>
      </Card>
    );
  }
  return (
    <div className="grid gap-4">
      {employees.map((emp) => (
        <Card key={emp.id} className="p-4 sm:p-6" data-testid={`employee-card-${emp.id}`}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="text-base sm:text-lg font-semibold">{emp.name}</h3>
                {emp.active === false ? (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">Inactive</span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">Active</span>
                )}
              </div>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                <span className="flex items-center gap-1 min-w-0 break-all"><Mail className="w-3.5 h-3.5 shrink-0" />{emp.email}</span>
                {emp.phone && <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{emp.phone}</span>}
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {(emp.permissions || []).map((p) => {
                  const meta = permissions.find((mp) => mp.key === p);
                  return (
                    <span key={p} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                      {meta?.label || p}
                    </span>
                  );
                })}
                {(!emp.permissions || emp.permissions.length === 0) && (
                  <span className="text-xs text-muted-foreground italic">No permissions granted</span>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button data-testid={`edit-employee-btn-${emp.id}`} variant="outline" size="sm" onClick={() => onEdit(emp)}>
                <Edit className="w-4 h-4 mr-1" /> Edit
              </Button>
              <Button data-testid={`delete-employee-btn-${emp.id}`} variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => onDelete(emp.id, emp.name)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
/* eslint-enable react/prop-types */
